import {
  UserRoles,
  applicantsActionsAccessRoles,
  timesheetsRoles,
  serviceRoles,
  isSystemAdminRoleOrHR,
} from '../../helpers/userRoles';
import { isDayforce, isMaof, isBench } from '../../settings/organizationSettings';
import { isProduction, getIsNetworkProduct, isDevOrReviewOrStagingOrDemo } from '../../settings';
import { PERMISSIONS } from '../../redux/permissions/constants';
import { canReadEngines, isPermissionGranted } from '../../redux/permissions/helpers';

const getOptions = (startAppSettings, metabaseSettings, role) => {
  const allowAdminHoursReview = startAppSettings?.organization_settings?.allow_admins_hours_review;
  const insightsVisible = !!metabaseSettings?.dashboard?.url;
  const isAgencyWorkersManagementEnabled = startAppSettings?.feature_flags?.agency_workers_management_enabled;
  const isInternalWorkersManagementEnabled = startAppSettings?.feature_flags?.internal_workers_management_enabled;
  const isConversionEnabled = startAppSettings?.feature_flags?.dayforce_worker_identity_conversion_enabled;
  const canViewAgencyWorkers = (isAgencyWorkersManagementEnabled || isConversionEnabled) && isSystemAdminRoleOrHR(role);

  // hide tab if both ff are off
  const showWorkforceTab = canViewAgencyWorkers || isInternalWorkersManagementEnabled;

  const options = [
    {
      key: 'dashboard/insights',
      dataCy: 'sidebar-dashboard/insights',
      label: 'sidebar.dashboard',
      hasNoAccess: [UserRoles.PROCUREMENT],
      isHidden: !insightsVisible,
    },
    {
      key: 'leads',
      dataCy: 'sidebar-leads',
      label: 'sidebar.leads',
      hasNoAccess: [UserRoles.PROCUREMENT],
    },
    {
      key: 'clients',
      dataCy: 'sidebar-clients',
      label: 'sidebar.clients',
      hasNoAccess: [UserRoles.PROCUREMENT],
      children: [
        {
          key: 'companies',
          dataCy: 'sidebar-companies',
          label: 'sidebar.clients_companies',
        },
        {
          key: 'divisions',
          dataCy: 'sidebar-divisions',
          label: 'sidebar.clients_divisions',
        },
        {
          key: 'units',
          dataCy: 'sidebar-units',
          label: 'sidebar.clients_units',
        },
        {
          key: 'subUnits',
          dataCy: 'sidebar-subUnits',
          label: 'sidebar.clients_subUnits',
        },
      ],
    },
    showWorkforceTab && {
      key: canViewAgencyWorkers ? 'agencyWorkers' : 'workforce',
      dataCy: 'sidebar-workforce',
      label: 'sidebar.workforce',
      hasNoAccess: [UserRoles.PROCUREMENT],
      children: !canViewAgencyWorkers && [
        {
          key: 'applicants',
          dataCy: 'sidebar-applicants',
          label: 'sidebar.applicants',
        },
        {
          key: 'talent',
          dataCy: 'sidebar-talent',
          label: 'sidebar.talent',
        },
        {
          key: 'denied',
          dataCy: 'sidebar-denied',
          label: 'sidebar.settings_denied',
          hasAccess: applicantsActionsAccessRoles,
        },
        {
          key: 'terminated',
          dataCy: 'sidebar-terminated',
          label: 'sidebar.settings_terminated',
          hasAccess: [UserRoles.ADMIN, UserRoles.SYSTEM_ADMIN, UserRoles.FINANCE],
        },
      ],
    },
    {
      key: 'jobs',
      dataCy: 'sidebar-jobs',
      label: 'sidebar.jobs',
      hasNoAccess: [UserRoles.PROCUREMENT],
      children: [
        {
          key: 'jobs?tab=past',
          dataCy: 'sidebar-jobs?tab=past',
          label: 'sidebar.jobs_past',
        },
        {
          key: 'jobs?tab=active',
          dataCy: 'sidebar-jobs?tab=active',
          label: 'sidebar.jobs_active',
        },
        {
          key: 'jobs?tab=future',
          dataCy: 'sidebar-jobs?tab=future',
          label: 'sidebar.jobs_future',
        },
        {
          key: 'jobs?tab=calendar_view',
          dataCy: 'sidebar-jobs?tab=calendar_view',
          label: 'sidebar.jobs_calendar',
        },
        {
          key: 'attentionRequired',
          dataCy: 'sidebar-attentionRequired',
          label: 'sidebar.jobs_attention_required',
          isDayforceOnly: true,
        },
      ],
    },
    {
      key: 'sessions',
      dataCy: 'sidebar-sessions',
      label: 'sidebar.sessions',
      hasNoAccess: [UserRoles.PROCUREMENT],
      isBenchOnly: true,
    },
    {
      key: 'agencies',
      dataCy: 'sidebar-agencies',
      label: 'sidebar.agencies',
      isHidden: (isDayforce() || isMaof()) && isProduction,
      children: [
        {
          key: 'agencies/invite',
          dataCy: 'sidebar-agencies/invite',
          label: 'sidebar.agencies_invite',
        },
        {
          key: 'agencies/active',
          dataCy: 'sidebar-agencies/active',
          label: 'sidebar.agencies_active',
        },
        {
          key: 'agencies/removed',
          dataCy: 'sidebar-agencies/removed',
          label: 'sidebar.agencies_deactivated',
        },
        {
          key: 'agencies/invoices',
          dataCy: 'sidebar-agencies/invoices',
          label: 'sidebar.agencies_invoices',
        },
      ],
    },
    {
      key: 'timesheets',
      dataCy: 'timesheets',
      label: 'sidebar.timesheets',
      hasAccess: timesheetsRoles,
      isHidden: !allowAdminHoursReview,
    },
    {
      key: 'settings',
      dataCy: 'sidebar-settings',
      label: 'sidebar.settings',
      hasNoAccess: [UserRoles.PROCUREMENT],
      isSettingsToggle: true,
    },
  ];

  return options;
};

// Settings menu categories with their menu items
export const getSettingsMenuOptions = () => [
  {
    key: 'policies',
    label: 'dash.sidebar.settings_sections.category_policies',
    items: [
      {
        key: 'accreditations',
        dataCy: 'sidebar-accreditations',
        label: 'sidebar.settings_accreditations',
        hasNoAccess: [...serviceRoles],
      },
      {
        key: 'attires',
        dataCy: 'sidebar-attires',
        label: 'sidebar.settings_attire',
        hasNoAccess: [...serviceRoles],
      },
      {
        key: 'cancellationPolicy',
        dataCy: 'sidebar-cancellationPolicy',
        label: 'sidebar.settings_cancellationPolicy',
        hasNoAccess: [UserRoles.ADMIN, UserRoles.CUSTOMER_SERVICE],
      },
      {
        key: 'holidayPricing',
        dataCy: 'sidebar-holidayPricing',
        label: 'sidebar.settings_holidayPricing',
      },
    ],
  },
  {
    key: 'configuration',
    label: 'dash.sidebar.settings_sections.category_configuration',
    items: [
      {
        key: 'markets',
        dataCy: 'sidebar-markets',
        label: 'sidebar.settings_markets',
      },
      {
        key: 'sectors',
        dataCy: 'sidebar-sectors',
        label: 'sidebar.settings_sectors',
        hasNoAccess: [...serviceRoles],
      },
      {
        key: 'districts',
        dataCy: 'sidebar-districts',
        label: 'sidebar.settings_districts',
      },
      {
        key: 'positions',
        dataCy: 'sidebar-positions',
        label: 'sidebar.settings_positions',
      },
      {
        key: 'organization',
        dataCy: 'sidebar-organization',
        label: 'sidebar.settings_organization',
        hasNoAccess: [UserRoles.PROCUREMENT],
      },
      {
        key: 'legalEntities',
        dataCy: 'sidebar-legal_entities',
        label: 'sidebar.settings_legal_entities',
        permission: PERMISSIONS.legal_entity.index,
      },
      {
        key: 'payroll',
        dataCy: 'sidebar-payroll',
        label: 'sidebar.settings_payroll',
        hasNoAccess: [...serviceRoles],
      },
      {
        key: 'checklists',
        dataCy: 'sidebar-checklists',
        label: 'sidebar.settings_checklists',
        permission: PERMISSIONS.checklists.index,
      },
      {
        key: 'dataUploads',
        dataCy: 'sidebar-dataUploads',
        label: 'sidebar.settings_data_uploads',
        permission: PERMISSIONS.bulk_upload.index,
      },
      {
        key: 'partners',
        dataCy: 'sidebar-partners',
        label: 'sidebar.settings_partners',
        hasNoAccess: [...serviceRoles],
        isHidden: getIsNetworkProduct(),
      },
      {
        key: 'algorithm',
        dataCy: 'sidebar-algorithm',
        label: 'sidebar.settings_algorithm',
        hasAccess: [
          UserRoles.SYSTEM_ADMIN,
          UserRoles.FINANCE,
          UserRoles.ACCOUNT_MANAGER,
          UserRoles.ADMIN,
          UserRoles.CUSTOMER_SERVICE,
          UserRoles.CUSTOMER_SERVICE_SUPERVISOR,
          UserRoles.MARKETING,
          UserRoles.RECRUITER,
          UserRoles.SALES,
        ],
      },
      {
        key: 'automationConfig',
        dataCy: 'sidebar-automationConfig',
        label: 'sidebar.settings_automationConfig',
        hasAccess: [
          UserRoles.SYSTEM_ADMIN,
          UserRoles.FINANCE,
          UserRoles.ACCOUNT_MANAGER,
          UserRoles.ADMIN,
          UserRoles.CUSTOMER_SERVICE,
          UserRoles.CUSTOMER_SERVICE_SUPERVISOR,
          UserRoles.MARKETING,
          UserRoles.RECRUITER,
          UserRoles.SALES,
        ],
      },
    ],
  },
  {
    key: 'pricing',
    label: 'dash.sidebar.settings_sections.category_pricing',
    items: [
      {
        key: 'pricing/rate-engine',
        dataCy: 'sidebar-rateEngine',
        label: 'sidebar.settings_rateEngine',
        permission: canReadEngines,
        isNew: true,
      },
      {
        key: 'pricing/rate-cards',
        dataCy: 'sidebar-rateCards',
        label: 'sidebar.settings_rateCards',
        permission: PERMISSIONS.pricing_settings.index,
        isNew: true,
      },
      {
        key: 'pricing/rate-simulator',
        dataCy: 'sidebar-rateSimulator',
        label: 'sidebar.settings_rateSimulator',
        permission: PERMISSIONS.pricing_settings.index,
        isNew: true,
      },
      {
        key: 'pricing/position-availability',
        dataCy: 'sidebar-positionAvailability',
        label: 'sidebar.settings_positionAvailability',
        permission: PERMISSIONS.pricing_settings.index,
        isNew: true,
      },
    ],
  },
  {
    key: 'users',
    label: 'dash.sidebar.settings_sections.category_users',
    items: [
      {
        key: 'users',
        dataCy: 'sidebar-users',
        label: 'sidebar.settings_users',
        hasNoAccess: [...serviceRoles],
      },
      {
        key: 'demoUsers',
        dataCy: 'sidebar-demoUsers',
        label: 'sidebar.settings_demoUsers',
        hasNoAccess: [...serviceRoles, UserRoles.ADMIN],
      },
    ],
  },
  {
    key: 'other',
    label: 'dash.sidebar.settings_sections.category_other',
    items: [
      {
        key: 'enterprise_organizations',
        dataCy: 'sidebar-enterprise_organizations',
        label: 'sidebar.organizations',
        hasAccess: [UserRoles.SYSTEM_ADMIN],
        isDayforceOnly: true,
        isDevOrReviewOrStagingOrDemoOnly: true,
      },
      {
        key: 'clientFeedbackOptions',
        dataCy: 'sidebar-clientFeedbackOptions',
        label: 'sidebar.settings_clientFeedbackOptions',
        hasNoAccess: [...serviceRoles],
      },
      {
        key: 'flexAppOptions',
        dataCy: 'sidebar-flexAppOptions',
        label: 'sidebar.settings_flexAppOptions',
        hasNoAccess: [
          UserRoles.CUSTOMER_SERVICE,
          UserRoles.ACCOUNT_MANAGER,
          UserRoles.MARKETING,
          UserRoles.SALES,
          UserRoles.RECRUITER,
        ],
      },
      {
        key: 'promoCodes',
        dataCy: 'sidebar-promoCodes',
        label: 'sidebar.settings_promoCodes',
        hasNoAccess: [
          UserRoles.CUSTOMER_SERVICE,
          UserRoles.ACCOUNT_MANAGER,
          UserRoles.RECRUITER,
          UserRoles.CUSTOMER_SERVICE_SUPERVISOR,
        ],
      },
      {
        key: 'user-guide',
        dataCy: 'sidebar-user-guide',
        label: 'sidebar.settings_guides',
        target: '_blank',
      },
      {
        key: 'referrals',
        dataCy: 'sidebar-referrals',
        label: 'sidebar.settings_referrals',
        hasNoAccess: [...serviceRoles, UserRoles.ADMIN],
      },
      {
        key: 'termsAndPrivacy',
        dataCy: 'sidebar-termsAndPrivacy',
        label: 'sidebar.settings_termsAndPrivacy',
        hasNoAccess: [...serviceRoles],
      },
    ],
  },
];

// Get all settings route keys for layout detection
export const getSettingsRoutes = () => {
  return getSettingsMenuOptions().flatMap(category => category.items.map(item => item.key));
};

// Helper to check if an item should be hidden based on access rules
export const isMenuItemHidden = (item, userRole, permissions = null) => {
  if (!item) return true;

  if (item.isDevOrReviewOrStagingOrDemoOnly && !isDevOrReviewOrStagingOrDemo) {
    return true;
  }

  if (item.hasAccess && !item.hasAccess.includes(userRole)) {
    return true;
  }

  if (item.hasNoAccess && item.hasNoAccess.includes(userRole)) {
    return true;
  }

  if (item.permission && !isPermissionGranted(permissions, item.permission)) {
    return true;
  }

  if (item.isDayforceOnly && !isDayforce()) {
    return true;
  }

  if (item.isBenchOnly && !isBench()) {
    return true;
  }

  return Boolean(item.isHidden);
};

// Get all multi-segment child menu keys (e.g., 'agencies/active')
export const getMultiSegmentKeys = (startAppSettings, metabaseSettings) => {
  const options = getOptions(startAppSettings, metabaseSettings);
  const keys = [];
  options.forEach(option => {
    if (option?.children) {
      option.children.forEach(child => {
        if (child.key.includes('/')) {
          keys.push(child.key);
        }
      });
    }
  });

  return keys;
};

// Resolve the sidebar menu sync-state from the current URL. Kept as a pure function
// so the multi-segment settings-key matching can be unit-tested in isolation.
// `settingsRoutes` are the settings item keys (may be multi-segment, e.g. 'pricing/rate-engine');
// `multiSegmentKeys` are the main-menu child keys containing '/' (e.g. 'agencies/active').
export const resolveSidebarRouteState = (pathname, settingsRoutes, multiSegmentKeys) => {
  const pathSegments = pathname.slice(1).split('/');
  const [firstSegment] = pathSegments;
  const twoSegmentPath = pathSegments.slice(0, 2).join('/');

  // Match a settings item key against the current URL. Settings keys may be
  // multi-segment (e.g. 'pricing/rate-engine'); match the full two-segment path
  // first, then fall back to a single first-segment key (e.g. 'markets').
  const matchedSettingsKey =
    settingsRoutes.find(key => twoSegmentPath === key || twoSegmentPath.startsWith(`${key}/`)) ||
    settingsRoutes.find(key => key === firstSegment);

  // Try matching multi-segment child keys from the main menu (e.g., 'agencies/active')
  const matchedKey = multiSegmentKeys.find(key => twoSegmentPath === key || twoSegmentPath.startsWith(`${key}/`));

  // We're on a settings route when the first URL segment matches the first
  // segment of any settings item key (covers 'markets' and 'pricing/rate-engine').
  const isOnSettingsRoute = settingsRoutes.some(key => key.split('/')[0] === firstSegment);

  return {
    firstSegment,
    matchedKey,
    currentPath: matchedSettingsKey || matchedKey || firstSegment,
    isOnSettingsRoute,
  };
};

export default getOptions;
